import { useEffect, useRef, useCallback } from "react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FLUSH_INTERVAL = 30000; // Send events every 30 seconds

/**
 * useIntegrityMonitor — Hook for monitoring assessment integrity
 *
 * Tracks:
 * - Tab/window visibility changes (tab switches)
 * - Paste events in inputs
 * - Keystroke counting (typed vs pasted chars)
 * - Time spent away from the tab
 *
 * Usage:
 *   const { trackPaste, trackKeystroke } = useIntegrityMonitor(candidateId, isActive);
 *
 * Then attach trackPaste/trackKeystroke to input event handlers.
 */
export function useIntegrityMonitor(candidateId, isActive = false) {
  const eventBuffer = useRef([]);
  const tabSwitchCount = useRef(0);
  const timeAwayStart = useRef(null);
  const totalTimeAway = useRef(0);
  const statsMap = useRef({}); // questionId -> {typed, pasted, pasteCount}

  // Flush events to backend
  const flushEvents = useCallback(async () => {
    if (!candidateId || eventBuffer.current.length === 0) return;

    const events = [...eventBuffer.current];
    eventBuffer.current = [];

    try {
      await fetch(`${API}/ta/integrity/events`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          candidate_id: candidateId,
          events: events,
        }),
      });
    } catch {
      // Re-add events to buffer on failure
      eventBuffer.current = [...events, ...eventBuffer.current];
    }
  }, [candidateId]);

  // Flush stats for a specific question
  const flushStats = useCallback(
    async (questionId) => {
      if (!candidateId || !questionId) return;
      const stats = statsMap.current[questionId];
      if (!stats) return;

      try {
        await fetch(`${API}/ta/integrity/stats`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            candidate_id: candidateId,
            question_id: questionId,
            typed_chars: stats.typed,
            pasted_chars: stats.pasted,
            paste_event_count: stats.pasteCount,
            tab_switches: tabSwitchCount.current,
            time_away_seconds: Math.round(totalTimeAway.current / 1000),
          }),
        });
      } catch {
        // Non-critical
      }
    },
    [candidateId]
  );

  // Tab visibility monitoring
  useEffect(() => {
    if (!isActive || !candidateId) return;

    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Tab switched away
        tabSwitchCount.current += 1;
        timeAwayStart.current = Date.now();
        eventBuffer.current.push({
          event_type: "tab_switch_away",
          event_data: {
            switch_count: tabSwitchCount.current,
            timestamp: new Date().toISOString(),
          },
        });
      } else {
        // Tab returned
        if (timeAwayStart.current) {
          const away = Date.now() - timeAwayStart.current;
          totalTimeAway.current += away;
          eventBuffer.current.push({
            event_type: "tab_switch_return",
            event_data: {
              away_duration_ms: away,
              total_away_ms: totalTimeAway.current,
              switch_count: tabSwitchCount.current,
              timestamp: new Date().toISOString(),
            },
          });
          timeAwayStart.current = null;
        }
      }
    };

    const handleWindowBlur = () => {
      if (!document.hidden) {
        tabSwitchCount.current += 1;
        timeAwayStart.current = Date.now();
        eventBuffer.current.push({
          event_type: "window_blur",
          event_data: {
            switch_count: tabSwitchCount.current,
            timestamp: new Date().toISOString(),
          },
        });
      }
    };

    const handleWindowFocus = () => {
      if (timeAwayStart.current) {
        const away = Date.now() - timeAwayStart.current;
        totalTimeAway.current += away;
        eventBuffer.current.push({
          event_type: "window_focus",
          event_data: {
            away_duration_ms: away,
            total_away_ms: totalTimeAway.current,
            timestamp: new Date().toISOString(),
          },
        });
        timeAwayStart.current = null;
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("blur", handleWindowBlur);
    window.addEventListener("focus", handleWindowFocus);

    // Periodic flush
    const flushTimer = setInterval(() => {
      flushEvents();
      // Flush stats for all tracked questions
      for (const qId of Object.keys(statsMap.current)) {
        flushStats(qId);
      }
    }, FLUSH_INTERVAL);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("blur", handleWindowBlur);
      window.removeEventListener("focus", handleWindowFocus);
      clearInterval(flushTimer);
      // Final flush on unmount
      flushEvents();
    };
  }, [isActive, candidateId, flushEvents, flushStats]);

  // Track paste event for a specific question
  const trackPaste = useCallback(
    (questionId, pastedText) => {
      if (!isActive || !candidateId) return;

      const len = pastedText?.length || 0;
      if (!statsMap.current[questionId]) {
        statsMap.current[questionId] = { typed: 0, pasted: 0, pasteCount: 0 };
      }
      statsMap.current[questionId].pasted += len;
      statsMap.current[questionId].pasteCount += 1;

      eventBuffer.current.push({
        event_type: "paste",
        question_id: questionId,
        event_data: {
          char_count: len,
          total_paste_count: statsMap.current[questionId].pasteCount,
          timestamp: new Date().toISOString(),
        },
      });
    },
    [isActive, candidateId]
  );

  // Track keystroke for a specific question
  const trackKeystroke = useCallback(
    (questionId, charCount = 1) => {
      if (!isActive || !candidateId) return;

      if (!statsMap.current[questionId]) {
        statsMap.current[questionId] = { typed: 0, pasted: 0, pasteCount: 0 };
      }
      statsMap.current[questionId].typed += charCount;
    },
    [isActive, candidateId]
  );

  return {
    trackPaste,
    trackKeystroke,
    tabSwitchCount: tabSwitchCount.current,
    flush: () => {
      flushEvents();
      for (const qId of Object.keys(statsMap.current)) {
        flushStats(qId);
      }
    },
  };
}
